/**
 * The rules of a logic minesweeper, ported from
 * `backend/apps/puzzletypes/minesweeper/rules.py` (parity through the shared fixtures), plus
 * the live contradictions, which only the client computes.
 *
 * A grid is a list of row strings. Nothing here ever sees the answer key.
 */
import { EMPTY, FLAG, HIDDEN, type Cell } from "./types";

const AROUND: Cell[] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

export const key = (r: number, c: number): string => `${r},${c}`;

export function neighbours([r, c]: Cell, rows: number, cols: number): Cell[] {
  return AROUND.map(([dr, dc]): Cell => [r + dr, c + dc]).filter(
    ([rr, cc]) => rr >= 0 && cc >= 0 && rr < rows && cc < cols,
  );
}

/** Every revealed cell and its number. */
export function numbersOf(cells: string[]): { cell: Cell; n: number }[] {
  const found: { cell: Cell; n: number }[] = [];
  cells.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1) {
      const char = row[c];
      if (char >= "0" && char <= "9") found.push({ cell: [r, c], n: Number(char) });
    }
  });
  return found;
}

export function isHidden(cells: string[], [r, c]: Cell): boolean {
  return (cells[r]?.[c] ?? HIDDEN) === HIDDEN;
}

export function hiddenCount(cells: string[]): number {
  return cells.reduce((acc, row) => acc + Array.from(row).filter((c) => c === HIDDEN).length, 0);
}

export function minesIn(layout: string[]): Set<string> {
  const found = new Set<string>();
  layout.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1) if (row[c] === FLAG) found.add(key(r, c));
  });
  return found;
}

export function countAround(cell: Cell, mines: Set<string>, rows: number, cols: number): number {
  return neighbours(cell, rows, cols).filter(([r, c]) => mines.has(key(r, c))).length;
}

/** Every revealed cell whose number differs from the mines around it, as `r,c` keys. */
export function numberMismatches(cells: string[], layout: string[]): Set<string> {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;
  const mines = minesIn(layout);
  return new Set(
    numbersOf(cells)
      .filter(({ cell, n }) => countAround(cell, mines, rows, cols) !== n)
      .map(({ cell: [r, c] }) => key(r, c)),
  );
}

/** Every rule the mine layout breaks, as the server's codes; empty when the answer is right. */
export function issues(cells: string[], mineCount: number, layout: string[]): string[] {
  const mines = minesIn(layout);
  const found: string[] = [];
  if ([...mines].some((k) => !isHidden(cells, k.split(",").map(Number) as Cell)))
    found.push("mine_on_revealed");
  if (numberMismatches(cells, layout).size > 0) found.push("number_mismatch");
  if (mines.size !== mineCount) found.push("mine_count_mismatch");
  return found;
}

/**
 * The fully revealed grid a mine layout implies: every non-mine cell and its number. For the
 * author, who hides cells from it afterwards.
 */
export function numbersFrom(layout: string[]): string[] {
  const rows = layout.length;
  const cols = layout[0]?.length ?? 0;
  const mines = minesIn(layout);
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) =>
      mines.has(key(r, c)) ? HIDDEN : String(countAround([r, c], mines, rows, cols)),
    ).join(""),
  );
}

export interface Contradictions {
  /** Numbers with more flags around them than they count, or too few undecided cells left. */
  numbers: Set<string>;
  /** More flags than mines, or too few undecided cells left to hold the rest. */
  total: boolean;
}

/**
 * What the board can no longer be, from the numbers, the mine count and the marks (`*`, `x`,
 * `.`) alone. Sound, not complete: every flag is a real mistake, and whatever needs reasoning
 * across two numbers stays silent (CLAUDE.md §16.4).
 */
export function contradictions(
  cells: string[],
  mineCount: number,
  marks: string[],
): Contradictions {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;
  const markAt = (cell: Cell) =>
    isHidden(cells, cell) ? (marks[cell[0]]?.[cell[1]] ?? EMPTY) : null;
  const numbers = new Set<string>();
  for (const { cell, n } of numbersOf(cells)) {
    let flags = 0;
    let open = 0;
    for (const around of neighbours(cell, rows, cols)) {
      const mark = markAt(around);
      if (mark === FLAG) flags += 1;
      else if (mark === EMPTY) open += 1;
    }
    if (flags > n || flags + open < n) numbers.add(key(cell[0], cell[1]));
  }
  let flags = 0;
  let open = 0;
  for (let r = 0; r < rows; r += 1)
    for (let c = 0; c < cols; c += 1) {
      const mark = markAt([r, c]);
      if (mark === FLAG) flags += 1;
      else if (mark === EMPTY) open += 1;
    }
  return { numbers, total: flags > mineCount || flags + open < mineCount };
}

/** Flags on hidden cells: what the "mines left" counter subtracts. */
export function flagCount(cells: string[], marks: string[]): number {
  let flags = 0;
  marks.forEach((row, r) => {
    for (let c = 0; c < row.length; c += 1)
      if (row[c] === FLAG && isHidden(cells, [r, c])) flags += 1;
  });
  return flags;
}

/** Numbers whose count of flags is met: they step back. */
export function doneNumbers(cells: string[], marks: string[]): Set<string> {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;
  const done = new Set<string>();
  for (const { cell, n } of numbersOf(cells)) {
    const flags = neighbours(cell, rows, cols).filter(
      (around) => isHidden(cells, around) && marks[around[0]]?.[around[1]] === FLAG,
    ).length;
    if (flags === n) done.add(key(cell[0], cell[1]));
  }
  return done;
}
